<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ContactSortMenu from './components/ContactSortMenu.vue';
import ContactMoreActions from './components/ContactMoreActions.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';

defineProps({
  showSearch: { type: Boolean, default: true },
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, required: true },
  buttonLabel: { type: String, default: '' },
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
  isSegmentsView: { type: Boolean, default: false },
  hasActiveFilters: { type: Boolean, default: false },
  isLabelView: { type: Boolean, default: false },
  isActiveView: { type: Boolean, default: false },
});

const emit = defineEmits([
  'search',
  'update:searchValue',
  'filter',
  'update:sort',
  'add',
  'import',
  'export',
  'createSegment',
  'deleteSegment',
]);
</script>

<template>
  <header class="sticky top-0 z-10">
    <div
      class="flex items-center justify-between w-full h-20 px-6 gap-2 mx-auto max-w-[60rem]"
    >
      <span class="text-xl font-medium truncate text-n-slate-12">
        {{ headerTitle }}
      </span>
      <div class="flex items-center flex-shrink-0 gap-4">
        <div v-if="showSearch" class="relative w-48 min-w-[8rem]">
          <Input
            :model-value="searchValue"
            type="text"
            :placeholder="$t('CONTACTS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
            :custom-input-class="[
              'h-8 w-full [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 !py-1 transition-[padding] duration-200 ease-in-out',
              searchValue ? 'ltr:!pl-8 rtl:!pr-8' : '',
              'ltr:!pr-8 rtl:!pl-8',
            ]"
            @input="emit('update:searchValue', $event.target.value)"
            @enter="emit('search', searchValue)"
          />
          <button
            v-if="searchValue"
            type="button"
            class="absolute z-10 top-1/2 -translate-y-1/2 ltr:left-0 rtl:right-0 w-8 h-8 flex items-center justify-center cursor-pointer text-black dark:text-white hover:opacity-80 transition-opacity duration-200 pointer-events-auto"
            :aria-label="$t('CONTACTS_LAYOUT.HEADER.CLEAR_SEARCH')"
            @click="
              emit('update:searchValue', '');
              emit('search', '');
            "
          >
            <span
              class="i-lucide-x size-4 inline-block text-current"
              aria-hidden="true"
            />
          </button>
          <button
            type="button"
            class="absolute z-10 top-1/2 -translate-y-1/2 ltr:right-0 rtl:left-0 w-8 h-8 flex items-center justify-center cursor-pointer text-black dark:text-white hover:opacity-80 transition-opacity duration-200 pointer-events-auto"
            :aria-label="$t('CONTACTS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
            @click="emit('search', searchValue)"
          >
            <span
              class="i-lucide-search size-4 inline-block text-current"
              aria-hidden="true"
            />
          </button>
        </div>
        <div class="flex items-center gap-2">
          <div v-if="!isLabelView && !isActiveView" class="relative">
            <Button
              id="toggleContactsFilterButton"
              :icon="
                isSegmentsView ? 'i-lucide-pen-line' : 'i-lucide-list-filter'
              "
              color="slate"
              size="sm"
              class="relative w-8"
              variant="ghost"
              @click="emit('filter')"
            >
              <div
                v-if="hasActiveFilters && !isSegmentsView"
                class="absolute top-0 right-0 w-2 h-2 rounded-full bg-n-brand"
              />
            </Button>
            <slot name="filter" />
          </div>
          <Button
            v-if="
              hasActiveFilters &&
              !isSegmentsView &&
              !isLabelView &&
              !isActiveView
            "
            icon="i-lucide-save"
            color="slate"
            size="sm"
            variant="ghost"
            @click="emit('createSegment')"
          />
          <Button
            v-if="isSegmentsView && !isLabelView && !isActiveView"
            icon="i-lucide-trash"
            color="slate"
            size="sm"
            variant="ghost"
            @click="emit('deleteSegment')"
          />
          <ContactSortMenu
            :active-sort="activeSort"
            :active-ordering="activeOrdering"
            @update:sort="emit('update:sort', $event)"
          />
          <ContactMoreActions
            @add="emit('add')"
            @import="emit('import')"
            @export="emit('export')"
          />
        </div>
        <div class="w-px h-4 bg-n-strong" />
        <ComposeConversation>
          <template #trigger="{ toggle }">
            <Button :label="buttonLabel" size="sm" @click="toggle" />
          </template>
        </ComposeConversation>
      </div>
    </div>
  </header>
</template>
